/**
 * decryptSharedItem — ECIES-decrypt a family-shared EncryptedVaultItem using
 * the recipient's family private key. The inverse of the web app's
 * `prepareShareForFamily`. Shared by the web app and the browser extension.
 */

import type { EncryptedVaultItem, VaultItem } from './index';

const HKDF_INFO = 'share-item-v1';

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (const c of b) s += String.fromCharCode(c);
  return btoa(s);
}
function bytesToBase64Url(b: Uint8Array): string {
  return bytesToBase64(b).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function base64ToBytes(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}
function base64UrlToBytes(b64url: string): Uint8Array {
  const pad = b64url.length % 4 === 0 ? '' : '='.repeat(4 - (b64url.length % 4));
  return base64ToBytes((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
}

export async function decryptSharedItem(
  encrypted: EncryptedVaultItem,
  familyPrivateKey: Uint8Array,
  familyPublicKey: Uint8Array,
): Promise<VaultItem> {
  if (!encrypted.ephemeralPublicKey) {
    throw new Error('decryptSharedItem: ephemeralPublicKey missing');
  }
  const familyPrivKey = await crypto.subtle.importKey(
    'jwk',
    {
      kty: 'OKP',
      crv: 'X25519',
      x: bytesToBase64Url(familyPublicKey),
      d: bytesToBase64Url(familyPrivateKey),
    } as JsonWebKey,
    { name: 'X25519' },
    false,
    ['deriveBits'],
  );

  const ephPubBytes = base64UrlToBytes(encrypted.ephemeralPublicKey);
  const ephPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: encrypted.ephemeralPublicKey },
    { name: 'X25519' },
    true,
    [],
  );

  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: ephPubKey },
    familyPrivKey,
    256,
  );

  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);

  const hkdfKeyMaterial = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );

  const ivBytes = base64ToBytes(encrypted.iv);
  const ctBytes = base64ToBytes(encrypted.ciphertext);
  const plainBytes = new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      aesKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
  return JSON.parse(new TextDecoder().decode(plainBytes)) as VaultItem;
}

export async function encryptSharedItem(
  item: VaultItem,
  familyPublicKey: Uint8Array,
): Promise<EncryptedVaultItem> {
  // 1. Fresh ephemeral X25519 keypair
  const ephKeyPair = (await crypto.subtle.generateKey(
    { name: 'X25519' },
    true,
    ['deriveBits'],
  )) as CryptoKeyPair;
  const ephPubJwk = await crypto.subtle.exportKey('jwk', ephKeyPair.publicKey);
  const ephPubBytes = base64UrlToBytes(ephPubJwk.x!);

  // 2. ECDH(ephPriv, familyPub) → shared secret
  const familyPubKey = await crypto.subtle.importKey(
    'jwk',
    { kty: 'OKP', crv: 'X25519', x: bytesToBase64Url(familyPublicKey) },
    { name: 'X25519' },
    true,
    [],
  );
  const sharedSecret = await crypto.subtle.deriveBits(
    { name: 'X25519', public: familyPubKey },
    ephKeyPair.privateKey,
    256,
  );

  // 3. HKDF: salt = ephPub || familyPub, info = "share-item-v1"
  const salt = new Uint8Array(64);
  salt.set(ephPubBytes, 0);
  salt.set(familyPublicKey, 32);
  const hkdfKeyMaterial = await crypto.subtle.importKey('raw', sharedSecret, 'HKDF', false, ['deriveKey']);
  const aesKey = await crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt, info: new TextEncoder().encode(HKDF_INFO) },
    hkdfKeyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );

  // 4. AES-GCM encrypt the item JSON as-is (id + timestamps preserved)
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plainBytes = new TextEncoder().encode(JSON.stringify(item));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, plainBytes as BufferSource),
  );

  return {
    id: item.id,
    ciphertext: bytesToBase64(ciphertext),
    iv: bytesToBase64(iv),
    clientUpdatedAt: item.updatedAt,
    orgId: item.orgId,
    ephemeralPublicKey: bytesToBase64Url(ephPubBytes),
  };
}
